import { useTranslationI18 } from "../../../../../../services/hooks/useTranslationI18";
import { OfferCardSize } from "../../../../../../utils/enums";
import { localizedNumber } from "../../../../../../utils/help/localization/localization";
import { capitalizeFirstLetter } from "../../../../../../utils/help/textPreparation/textPreparation";
import LoyaltyOffer from "../../../../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import {
  AddIcon,
  Confetti,
  MinusIcon,
} from "../../../../../global/Icons/Icons";
import {
  LockIconCircle,
  MileStone,
} from "../../../../../global/Icons/loyalty.icons";
import { LoyaltyOfferIcon } from "../../../../../global/LoyaltyOfferIcon/LoyaltyOfferIcon";
import {
  OfferGraphicCollage,
  useHasCollageGraphics,
} from "../../../../../global/OfferGraphicCollage/OfferGraphicCollage";
import { PChip } from "../../../../../shared/PChip/PChip";
import { PImage } from "../../../../../shared/PImage/PImage";
import styles from "../OfferCardV2.module.scss";
import { useLoyaltyOfferCard } from "./useLoyaltyOfferCard";

export const LoyaltyOfferCard = (props: {
  offer: LoyaltyOffer;
  isActiveOffer: boolean;
  /** Server-confirmed applied state — controls green badge only. */
  showAppliedBadge?: boolean;
  size: OfferCardSize;
  hideRedeemBtn?: boolean;
  disabled?: boolean;
  pointsToUnlock?: number;
  hasMinWidth?: boolean;
  /** Renders the Figma tier-surface treatment: drop shadow, no idle stroke. */
  tierSurface?: boolean;
  alwaysShowBottomSection?: boolean;
  onClick?: () => void;
}) => {
  const {
    offer,
    isActiveOffer,
    showAppliedBadge = isActiveOffer,
    size,
    hideRedeemBtn,
    hasMinWidth = true,
    tierSurface = false,
    alwaysShowBottomSection = false,
  } = props;

  const {
    isOfferUnavailable,
    statusChipText,
    isLocked,
    isMainDark,
    hasBottomSection,
    hasBackgroundText,
    offerTitle,
    offerBgText,
    offerDetails,
    centralOfferImageUrl,
    collage,
    shouldUseGiftIconForOfferGraphics,
    stateLabel,
    handleCardClick,
    createMarkup,
  } = useLoyaltyOfferCard(props);

  /*
   * Only once the collage has images to draw: a BOGO whose items have all
   * lost their photographs falls back to the graphics below rather than
   * leaving the card blank.
   */
  const hasCollage = useHasCollageGraphics(
    collage?.graphics,
    collage?.includeEntityIds
  );

  /** The collage replaces all three of the graphics below when it is present. */
  const showBackgroundText = hasBackgroundText && !hasCollage;
  /** Background text wins over central image when both exist (legacy OfferCard parity). */
  const showCentralOfferImage =
    Boolean(centralOfferImageUrl) && !hasBackgroundText && !hasCollage;
  /** Gift icon when multiple image graphics, or single graphic with no `entityImageLookup` URL. */
  const shouldRenderGiftIcon =
    shouldUseGiftIconForOfferGraphics && !hasBackgroundText && !hasCollage;

  const offerTitleText = offerTitle?.map((item) => item.text).join(" ") ?? "";
  const bottomSectionText =
    alwaysShowBottomSection && !offerTitleText ? offerDetails : offerTitleText;

  const { t } = useTranslationI18();
  const chipSize = size === OfferCardSize.small ? "sm" : "md";

  const appliedText = t("common:offer-applied");

  return (
    <div
      data-testid={`cart-gift-card-v2-${offer.offerCategory}`}
      className={`${styles.gift_card} ${styles[OfferCardSize[size]]} ${
        isActiveOffer ? styles.active : ""
      } ${tierSurface ? styles.tier_surface : ""} ${
        isLocked || isOfferUnavailable ? styles.locked : ""
      } ${isLocked && isMainDark ? "bg__zero invert" : ""}`}
      style={{ ...(!hasMinWidth && { minWidth: "unset !important" }) }}
      onClick={handleCardClick}
      key={offer.id}
    >
      <div
        className={`${styles.main_section} ${
          !showCentralOfferImage ? styles.no_bg : ""
        }`}
      >
        <span className={styles.value}>
          {!hideRedeemBtn && !isOfferUnavailable && !isLocked && (
            <div
              data-testid={`${stateLabel}-offer-v2`}
              className={`${styles.state} ${
                isActiveOffer ? styles.active_state : ""
              }`}
            >
              {isActiveOffer ? <MinusIcon /> : <AddIcon />}
            </div>
          )}

          {statusChipText && (
            <PChip
              size={chipSize}
              variant={isOfferUnavailable ? "alert" : "primary"}
              className={`${styles.details_chip} ${
                isOfferUnavailable ? styles.chip_alert : ""
              }`}
            >
              {capitalizeFirstLetter(statusChipText)}
            </PChip>
          )}

          {isLocked ? (
            <div
              data-testid="lock-icon"
              className={`${styles.floating_button} ${styles.lock}`}
            >
              <LockIconCircle />
            </div>
          ) : null}

          <div className={styles.central_section}>
            <div className={styles.confetti}>
              <Confetti />
            </div>

            <div className={styles.central_section_foreground}>
              {hasCollage && collage && (
                <div className={styles.offer_collage}>
                  <div className={styles.offer_collage_box}>
                    <OfferGraphicCollage
                      graphics={collage.graphics}
                      includeEntityIds={collage.includeEntityIds}
                    />
                  </div>
                </div>
              )}

              {showBackgroundText && (
                <div className={styles.bg_text}>
                  {offerBgText?.map((item, index) => (
                    <span
                      key={index}
                      className={
                        item.useAlternateStyle ? styles.alternate_style : ""
                      }
                    >
                      <span dangerouslySetInnerHTML={createMarkup(item.text)} />
                    </span>
                  ))}
                </div>
              )}

              {showCentralOfferImage && centralOfferImageUrl ? (
                <div className={styles.img_container}>
                  <PImage
                    src={centralOfferImageUrl}
                    alt=""
                    fill
                    className={styles.img}
                    style={{
                      objectFit: "cover",
                      objectPosition: "center",
                      overflow: "visible",
                    }}
                  />
                </div>
              ) : shouldRenderGiftIcon ? (
                <div className={styles.offer_icon}>
                  <LoyaltyOfferIcon
                    offer={offer}
                    backgroundIconColor="primary"
                    iconOnly
                    /* A BOGO or bundle keeps its own glyph; `giftIcon` would
                       swap it for the generic free-item one. */
                    giftIcon={!collage}
                    width={50}
                  />
                </div>
              ) : null}

              {showAppliedBadge && (
                <div
                  className={`${styles.green_tick}`}
                  data-testid="green-tick"
                >
                  <span>{appliedText}</span>
                </div>
              )}
            </div>
          </div>
        </span>

        {(hasBottomSection || alwaysShowBottomSection) && (
          <div className={styles.bottom_section}>
            {Boolean(offer.milestonePoints) && (
              <div className={styles.points}>
                <span>{localizedNumber(offer.milestonePoints)}</span>
                <MileStone />
              </div>
            )}
            <div className={styles.text_section}>
              <div
                className={`${styles.main_text} ${
                  offer.milestonePoints ? styles.tighter : ""
                }`}
              >
                {bottomSectionText}
              </div>
              {size === OfferCardSize.large && offerDetails && (
                <span className={styles.details_text}>{offerDetails}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
